/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@store/store";
import {
  setDocumentsAnexo1ConyugeSecondGuarantee,
  setDocumentsAnexo1SecondGuarantee,
  setDocumentsConyugeSecondGuarantee,
  setDocumentsLoading,
  setDocumentsSecondGuarantee,
} from "@store/slices/segundoAvalSlice";
import { BofuTabDocument } from "@interfaces/response";
import { useLazyGetTabsConfigQuery } from "@api/catalogs";
import { identifyPersons } from "@helpers/funciones";
import { UUID } from "crypto";
import { generateDocumentConfig, identityDocumentsByPerson, validateDocumentStructure } from "./functions";
import * as Sentry from "@sentry/react";
import {
  useLazyGetDocumentAppIdSecondGuaranteeQuery,
  useLazyGetPersonByIdGuaranteeQuery,
  useLazyGetPersonsSecondGuaranteeQuery,
} from "@api/second_guarantee_api/secondGuaranteeApi";
import { DOCUMENTS_CATALOG } from "@helpers/constantes";

interface Props {
  isMarriageAval?: boolean;
}

const SegundoAvalDocuments = ({ isMarriageAval }: Props) => {
  const dispatch = useDispatch();

  const shareholder = useSelector((state: RootState) => state.secondGuarante.guarantee);
  const applicationData = useSelector((state: RootState) => state.register.application);

  const [getTabsConfig] = useLazyGetTabsConfigQuery();
  const [getDocsByAppId] = useLazyGetDocumentAppIdSecondGuaranteeQuery();
  const [getPersons] = useLazyGetPersonsSecondGuaranteeQuery();
  const [getPerson] = useLazyGetPersonByIdGuaranteeQuery();

  const generateTabsStructure = async () => {
    try {
      dispatch(setDocumentsLoading(true));

      const [tabs_config, persons, documents] = await Promise.all([
        getTabsConfig({ taxcode: String(applicationData.tax_systems?.code) }).unwrap(),
        getPersons({
          loanApplicationId: shareholder.loan_application_id as UUID,
          id: shareholder.id as UUID,
        }).unwrap(),
        getDocsByAppId({
          loanApplicationId: shareholder.loan_application_id as UUID,
          status: true,
        }).unwrap(),
      ]);

      if (tabs_config.length === 0) {
        dispatch(setDocumentsLoading(false));
        return;
      }

      const { segundo_aval } = identifyPersons(persons);
      const { docs_segundo_aval } = identityDocumentsByPerson({
        documents,
        segundo_aval,
      });

      const { anexo_1 } = DOCUMENTS_CATALOG;

      const valid_documents: BofuTabDocument[] = [];
      const valid_documents_conyuge: BofuTabDocument[] = [];

      // Filtrar solo los documentos con relationship "aval"
      tabs_config.forEach((tab) => {
        tab.documents
          .filter((item) => item.relationship === "aval" && validateDocumentStructure(item))
          .forEach((docItem) => {
            const document_value = generateDocumentConfig({
              docItem,
              docs_account: [],
              docs_representante: [],
              docs_aval: docs_segundo_aval,
              docs_conyuge_aval: [],
              docs_segundo_aval,
              clientDetailsId: shareholder.client_details_id as UUID,
              representante: undefined,
              aval: segundo_aval,
              conyuge_aval: undefined,
              segundo_aval,
            });

            valid_documents.push(document_value);
          });
      });

      const doc_anexo_1 = docs_segundo_aval.find((item) => item.file_code === anexo_1.code);
      const doc_anexo_1_conyuge = docs_segundo_aval.find((item) => item.file_code === anexo_1.code);

      dispatch(setDocumentsAnexo1SecondGuarantee(doc_anexo_1));
      dispatch(setDocumentsSecondGuarantee(valid_documents));

      if (isMarriageAval) {
        const conyuge_aval = await getPerson(String(segundo_aval.spouse_id)).unwrap();
        const { docs_conyuge_aval } = identityDocumentsByPerson({
          documents,
          conyuge_aval,
        });
        tabs_config.forEach((tab) => {
          tab.documents
            .filter((item) => item.relationship === "conyuge_aval" && validateDocumentStructure(item))
            .forEach((docItem) => {
              const document_value = generateDocumentConfig({
                docItem,
                docs_account: [],
                docs_representante: [],
                docs_aval: [],
                docs_conyuge_aval: docs_conyuge_aval,
                docs_segundo_aval: [],
                clientDetailsId: shareholder.client_details_id as UUID,
                representante: undefined,
                aval: undefined,
                conyuge_aval: conyuge_aval,
                segundo_aval: undefined,
              });

              valid_documents_conyuge.push(document_value);
            });
        });

        dispatch(setDocumentsAnexo1ConyugeSecondGuarantee(doc_anexo_1_conyuge));
        dispatch(setDocumentsConyugeSecondGuarantee(valid_documents_conyuge));
      }
    } catch (error) {
      console.error("Ha ocurrido un error al generar los documentos", error);
      Sentry.captureException(error, {
        extra: {
          component: "SegundoAval-generateTabsStructure",
        },
      });
    } finally {
      dispatch(setDocumentsLoading(false));
    }
  };

  useEffect(() => {
    generateTabsStructure();
  }, []);

  return null;
};

export default SegundoAvalDocuments;
